import { useApi } from "@platform/api";
import { buildEnrollmentReview, rateQueryKeys } from "@platform/btx-rates";
import {
  proposalPlanGroupToRatesBenefitPlanGroup,
  proposalQueryKeys,
} from "@platform/mpx-quotes-grid";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";

import type {
  BenefitPlanGroup,
  EnrollmentDecision,
  EnrollmentReview,
} from "@platform/btx-rates";
import type {
  ProposalPlanGroup,
  ProposalRatesGridData,
} from "@platform/mpx-quotes-grid";
import type { AxiosInstance } from "axios";

/** Shown in the dialog when the grid does not name the carrier that quoted a group. */
const UNNAMED_CARRIER = "The carrier";

/** Request body for PATCH /btx/v1/proposal-plan-groups/:id */
interface UpdateProposalPlanGroupEnrollmentDecisionRequest {
  enrollmentDecision: EnrollmentDecision;
}

const patchEnrollmentDecision = async (
  axiosInstance: AxiosInstance,
  groupId: string,
  decision: EnrollmentDecision,
): Promise<ProposalPlanGroup> => {
  const body: UpdateProposalPlanGroupEnrollmentDecisionRequest = {
    enrollmentDecision: decision,
  };
  const response = await axiosInstance.patch<ProposalPlanGroup>(
    `btx/v1/proposal-plan-groups/${groupId}`,
    body,
  );

  return response.data;
};

export interface UseQuotedEnrollmentReviewOptions {
  rfpId: string;
  /** What the rates grid shows: its plan groups, and the same groups by carrier. Absent until loaded. */
  ratesGridData: ProposalRatesGridData | null | undefined;
  onSaveError?: (err: Error) => void;
}

export interface QuotedEnrollmentReviewState {
  /** The review the dialog shows; undefined while it is closed. */
  review: EnrollmentReview | undefined;
  carrierName: string;
  isApplying: boolean;
  open: (group: BenefitPlanGroup) => void;
  close: () => void;
  apply: (decision: EnrollmentDecision) => void;
}

/**
 * Owns the Review Quoted Enrollment dialog: which plan group it shows, the carrier that quoted
 * it, and saving the analyst's choice for the whole group.
 *
 * The group is looked up on every render rather than kept in state, so the dialog follows the
 * proposals as they refetch.
 */
export const useQuotedEnrollmentReview = ({
  rfpId,
  ratesGridData,
  onSaveError,
}: UseQuotedEnrollmentReviewOptions): QuotedEnrollmentReviewState => {
  const axiosInstance = useApi();
  const queryClient = useQueryClient();
  const [groupId, setGroupId] = useState<string>();

  const group = ratesGridData?.benefitPlanGroups.find(
    (candidate) => candidate.id === groupId,
  );
  const carrierName =
    ratesGridData?.carrierGroups.find((carrier) =>
      carrier.benefitPlanGroups.some((candidate) => candidate.id === groupId),
    )?.carrierName ?? UNNAMED_CARRIER;

  const { mutate, isPending } = useMutation({
    mutationFn: (decision: EnrollmentDecision) =>
      patchEnrollmentDecision(axiosInstance, groupId ?? "", decision),
    onSuccess: async (savedGroup) => {
      // The rate cells read each plan's schedules from this cache once it holds any, and a
      // refetch does not replace them. The saved group carries the figures the choice set.
      proposalPlanGroupToRatesBenefitPlanGroup(savedGroup).benefitPlans.forEach(
        (plan) =>
          queryClient.setQueryData(
            rateQueryKeys.schedules(plan.id),
            plan.rateSchedule ?? [],
          ),
      );

      await queryClient.invalidateQueries({
        queryKey: proposalQueryKeys.all(rfpId),
      });
      setGroupId(undefined);
    },
    onError: (err: Error) => onSaveError?.(err),
  });

  const open = useCallback(
    (target: BenefitPlanGroup) => setGroupId(target.id),
    [],
  );
  const close = useCallback(() => setGroupId(undefined), []);

  return {
    review: group ? buildEnrollmentReview(group) : undefined,
    carrierName,
    isApplying: isPending,
    open,
    close,
    apply: mutate,
  };
};
